import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FilterBar } from '../../../shared/common/FilterBar';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import ProductList from '../../../shared/common/ProductList';
import CatalogueListItem from '../../catalogue/CatalogueListItem';
import Pagination from '../../../shared/common/Pagination';
import { LanguageProvider } from '../../../contexts/language';
import { CatalogueProductTile } from '../../catalogue/CatalogueProductTile';
import { BulkActionButtons } from '../../../shared/search/BulkActionButtons';
import { SelectionList } from '../../../../models/SelectionList';
import { ProductSorting } from '../../../../models/Product';
import { useParams } from 'react-router';
import { PriceTier } from '../../../../models/PriceTier';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { useTranslation } from 'react-i18next';
import { ExcludeProductSelection } from '../../../shared/product-selection/ProductSelectionTypes';
import { MAX_SELECT, useProductSelection } from '../../../shared/product-selection/useProductSelection';
import { TYPE_WARNING } from '../../../shared/notification/actions';
import { useAddToList } from '../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../api/shopping-cart/useAddToCart';
import { CheckBeforeAddingModal } from '../../../shared/common/CheckBeforeAddingModal';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../shared/common/useListContentChecks';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';
import { SubscriptionPeriodType } from '../../../../models/Article';
import { UnavailableProductsModal } from '../../../shared/common/UnavailableProductsModal';
import { ProductSelectionHeader } from '../../../shared/product-selection/ProductSelectionHeader';
import { CatalogueMenuActionButtons } from '../../catalogue/CatalogueMenuActionButtons';
import { nothingSelected } from '../../../shared/product-selection/ProductSelectionUtils';
import { ContainerInnerWithColumns } from '../../../shared/common/ContainerInner';
import { LoadingWrapper } from '../../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { useCreateNewSelectionList } from '../../../api/selection-list/useCreateNewSelectionList';
import { useSelectionLists } from '../../../api/selection-list/useSelectionLists';
import { useDisplayTypeSearchParam } from '../../../shared/utils/useDisplayTypeSearchParam';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../shared/loanFormat/constants';
import { loanFormatKeyToObject } from '../../../shared/loanFormat/mappers';
import { useAuthentication } from '../../../app-store/authentication/useAuthentication';
import { showNotification } from '../../../shared/notification/toastNotification';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../../shared/filter/useProductFilters';
import {
    invalidateAllProductsProducts,
    useAllProductsProducts
} from '../../../api/all-products/useAllProductsProducts';
import { useAddBulkToCart } from '../../catalogue/useAddBulkToCart';
import { useAddBulkToSelectionlist } from '../../catalogue/useAddBulkToSelectionlist';
import { fetchAllProductsAggregations } from '../../../api/all-products/fetchAllProductsAggregations';
import { fetchAllProductsAggregationsQuickSearch } from '../../../api/all-products/fetchAllProductsAggregationsQuicksearch';

const StFilterBar = styled(FilterBar)`
    margin: 52px 0 40px 0;
`;

export const DEFAULT_PAGE_SIZE = 60;

export const LanguageProductsPage: FC = () => {
    const { t } = useTranslation();
    const authentication = useAuthentication();
    const { productSelection, cancelSelection, toggleSelectionMode, onSelectButtonClick, selectAll } =
        useProductSelection();
    const {
        languageId: selectedLanguage = '',
        loanFormat: loanFormatPathParam = '',
        shopLanguage
    } = useParams<'languageId' | 'loanFormat' | 'shopLanguage'>();

    const loanFormat = loanFormatKeyToObject(loanFormatPathParam);
    const language = decodeURIComponent(selectedLanguage);

    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, sortBy, pageSize } = usePaginationAndSortParams({
        defaultPageSize: DEFAULT_PAGE_SIZE,
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD
    });

    const { productFilter } = useProductFilters();

    const { value: ageCategory } = useAgeCategorySearchParam();
    const { display } = useDisplayTypeSearchParam();

    const { mutate: createNewList } = useCreateNewSelectionList();
    const { selectionLists } = useSelectionLists();

    const { data: productData, isLoading: isLoadingProducts } = useAllProductsProducts({
        loanFormat: loanFormat.requestKey!,
        ageCategory,
        pagination: { sortBy, page, pageSize },
        filterMap: {
            ...productFilter,
            language: { include: [language] }
        },
        shopLanguage
    });

    const products = productData?.hits ?? [];
    const totalProducts = productData?.totalHits ?? 0;

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory).filter(filterGroup => filterGroup !== 'language'),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => {
            return [
                'languageEntryPage',
                'aggregations',
                loanFormat,
                filters,
                ageCategory,
                expandedFilterGroups,
                shopLanguage
            ];
        },
        [ageCategory, loanFormat, shopLanguage]
    );

    const fetchAggregations = useCallback<FetchAggregations>(
        options => {
            return fetchAllProductsAggregations({
                ...options,
                loanFormat: loanFormat.requestKey!,
                filters: {
                    ...options.filters,
                    language: { include: [language] }
                },
                ageCategory,
                shopLanguage
            });
        },
        [ageCategory, language, loanFormat.requestKey, shopLanguage]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchAllProductsAggregationsQuickSearch({
                ...options,
                loanFormat: loanFormat.requestKey!,
                filters: {
                    ...options.filters,
                    language: { include: [language] }
                },
                ageCategory,
                shopLanguage
            }),
        [ageCategory, language, loanFormat.requestKey, shopLanguage]
    );
    //endregion

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => invalidateAllProductsProducts().then() });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const { isLoading: isLoadingAddProductsToCart, addToCartBulk: addBulkProductSelectionToCart } = useAddBulkToCart({
        loanFormat,
        ageCategory,
        shopLanguage,
        productFilter: { ...productFilter, language: { include: [language] } },
        onSuccess: () => {
            invalidateAllProductsProducts().then();
            cancelSelection();
        }
    });

    const { isLoading: isLoadingAddProductsToSelectionlist, addToListBulk: addBulkProductSelectionToList } =
        useAddBulkToSelectionlist({
            productFilter: { ...productFilter, language: { include: [language] } },
            loanFormat,
            ageCategory,
            shopLanguage,
            onSuccess: () => {
                cancelSelection();
            }
        });

    const onBulkAddToList = async (
        list: SelectionList,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: totalProducts
        };

        if (productSelection.totalCount > MAX_SELECT) {
            showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING);
            throw new Error('max selection exceeded');
        }

        return addBulkProductSelectionToList(
            list.id,
            priceTier,
            subscriptionPeriodType,
            performChecks,
            productSelection
        );
    };

    const onBulkAddToCart = async (
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: totalProducts
        };

        if (productSelection.totalCount > MAX_SELECT) {
            showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING);
            throw new Error('max selection exceeded');
        }

        return addBulkProductSelectionToCart(priceTier, subscriptionPeriodType, performChecks, productSelection);
    };

    const loading = isLoadingProducts || isLoadingAddProductsToCart || isLoadingAddProductsToSelectionlist;

    return (
        <LanguageProvider value={language}>
            {loading ? (
                <LoadingWrapper>
                    <LoadingOverlay loading={loading} />
                </LoadingWrapper>
            ) : null}
            <ContainerInnerWithColumns hasTopMargin={false}>
                <ProductSelectionHeader
                    header={
                        <BulkActionButtons
                            listLabel={t('addToList')}
                            cartLabel={t('addToCart')}
                            theme="language"
                            selectionLists={selectionLists}
                            onCreateNewListClicked={createNewList}
                            onBulkAddToList={onBulkAddToList}
                            onBulkAddToCart={onBulkAddToCart}
                            onlyEpressProducts={loanFormat.requestKey === LOANFORMAT_EPRESS_OBJECT.requestKey}
                        />
                    }
                    menu={
                        <CatalogueMenuActionButtons
                            onBulkAddToCart={(priceTier, subscriptionPeriodType, performChecks) =>
                                addBulkProductSelectionToCart(
                                    priceTier,
                                    subscriptionPeriodType,
                                    performChecks,
                                    productSelection
                                )
                            }
                            onBulkAddToList={(orderList, priceTier, subscriptionPeriodType, performChecks) =>
                                addBulkProductSelectionToList(
                                    orderList.id,
                                    priceTier,
                                    subscriptionPeriodType,
                                    performChecks,
                                    productSelection
                                )
                            }
                            defaultPriceTier={
                                loanFormat.requestKey === LOANFORMAT_EPRESS_OBJECT.requestKey
                                    ? { lendingModel: 'SIM', level: 'MIN' }
                                    : { lendingModel: 'OCOU', level: 'MIN' }
                            }
                            isNothingSelected={nothingSelected(productSelection)}
                            onlyEpressProducts={loanFormat.requestKey === LOANFORMAT_EPRESS_OBJECT.requestKey}
                        />
                    }
                />

                <StFilterBar
                    sortBy={sortBy}
                    totalProducts={totalProducts}
                    displayType={display}
                    pageSize={pageSize}
                    hideGroupBy={true}
                    hideSelectButtons={false}
                    productSelection={productSelection}
                    toggleSelectionMode={toggleSelectionMode}
                    selectAll={() => selectAll(totalProducts)}
                />
                <div className="content-area-inner flex-ca">
                    {displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null}
                    <div className="ca-main">
                        {products.length === 0 && !loading ? (
                            <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                        ) : (
                            <div className="ca-main-inner">
                                <ProductList
                                    loading={loading}
                                    products={products}
                                    displayType={display}
                                    loanFormatOverride={loanFormat}
                                    onAddPrice={changePrice}
                                    isProductSelectable={true}
                                    productSelection={productSelection}
                                    onSelectProduct={onSelectButtonClick}
                                >
                                    {({ key, isListView, ...value }) =>
                                        isListView ? (
                                            <CatalogueListItem {...value} key={key} shopLanguage={shopLanguage} />
                                        ) : (
                                            <CatalogueProductTile {...value} key={key} shopLanguage={shopLanguage} />
                                        )
                                    }
                                </ProductList>

                                <Pagination totalItems={totalProducts} pageSize={pageSize} />
                            </div>
                        )}
                    </div>
                    <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
                    <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
                </div>
            </ContainerInnerWithColumns>
        </LanguageProvider>
    );
};
